import Button from '@/Components/Button';
import MetaHead from '@/Components/MetaHead';
import Link from 'next/link';
const { parse } = require('rss-to-json');

const fetchMediumBlogUrl = 'https://medium.com/feed/@fyllo_jobs';

export default function Jobs({ Job, id }) {
  // Handle case where no job data is available
  if (!Job || Object.keys(Job).length === 0) {
    return (
      <main className="py-8 px-4 mx-auto max-w-screen-lg text-left lg:py-16 lg:px-6">
        <h2 className="text-fyllo-800 mb-4 md:mb-8 text-4xl font-extrabold tracking-tight leading-none md:text-5xl xl:text-4xl text-center">
          Job Not Found
        </h2>
        <p className="mb-6 font-light text-gray-500 lg:mb-8 md:text-lg lg:text-xl text-center">
          Sorry, this job posting is no longer available.
        </p>
        <div className="flex justify-center">
          <Link href="/careers">
            <div className="max-w-max px-4 py-1 font-medium border-2 hover:text-white hover:border-fyllo-800 hover:bg-fyllo-800 rounded-full">
              Back to Careers
            </div>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="py-8 px-4 mx-auto max-w-screen-lg text-left lg:py-16 lg:px-6 ">
      <MetaHead 
        title={Job.title + ' | Fyllo'} 
        keywords={Job.categories?.join(',')} 
        description={Job.title} 
      />
      <div className="mx-auto ">
        <h2 className="text-fyllo-800 mb-4 md:mb-8 text-4xl font-extrabold tracking-tight leading-none md:text-5xl xl:text-4xl text-center">
          {Job.title}
        </h2>
        <p className="mb-6 font-light text-gray-500 lg:mb-8 md:text-lg lg:text-xl text-center">
          {/* We have researched and worked on 20+ crops across horticulture, plantation , fibres and agriculture. We have
                    100+ models for precise irrigation, fertigation and disease and pest models. */}
        </p>
      </div>

      <div
        className="mx-auto max-w-screen-lg p-2 prose prose-sm prose-stone lg:prose-xl prose-img:rounded-xl prose-headings:text-gray-700 prose-headings:font-bold prose-a:text-blue-600"
        dangerouslySetInnerHTML={{
          __html: Job.description,
        }}
      ></div>
      <div className="flex justify-center">
        <Link href={{ pathname: '/careers/jobs/apply', query: { job: Job.title } }}>
          <div className="max-w-max px-4 py-1 font-medium border-2 hover:text-white hover:border-fyllo-800 hover:bg-fyllo-800 rounded-full">
            Apply Now
          </div>
        </Link>
      </div>
    </main>
  );
}

// Helper function to fetch RSS with retry and backoff
async function fetchRSSWithRetry(url, maxRetries = 3, initialDelay = 1000) {
  let retries = 0;
  let delay = initialDelay;
  
  while (retries < maxRetries) {
    try {
      return await parse(url, {
        headers: {
         'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/110.0.0.0 Safari/537.36'
        }
     });
    } catch (error) {
      if (error?.response?.status === 429 && retries < maxRetries - 1) {
        // Wait with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay));
        retries++;
        delay *= 2; // Exponential backoff
        continue;
      }
      // For other errors or final retry, return empty data
      console.error(`Failed to fetch RSS from ${url}:`, error);
      return { items: [] };
    }
  }
}

export async function getStaticPaths() {
  try {
    const response = await fetchRSSWithRetry(fetchMediumBlogUrl);
    const blogs = response?.items || [];
    
    const paths = blogs.map((blog) => ({
      params: { jobId: blog.title.replace("/", "-") },
    }));

    return {
      paths,
      // Change to true to enable fallback rendering
      fallback: 'blocking',
    };
  } catch (error) {
    console.error("Error in getStaticPaths:", error);
    // Return empty paths to allow build to continue
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
}

export async function getStaticProps({ params }) {
  try {
    const response = await fetchRSSWithRetry(fetchMediumBlogUrl);
    
    const blogs = (response?.items || []).map(blog => ({
      ...blog,
      title: blog.title,
      description: blog?.description || blog?.content || "",
    }));
    
    const id = params.jobId;
    let job = blogs.find((blog) => id === blog.title.replace("/", "-"));
    
    // If job not found, return notFound flag
    if (!job) {
      return {
        notFound: true,
        revalidate: 60, // Revalidate every minute
      };
    }

    return {
      props: { 
        Job: job, 
        id 
      },
      revalidate: 60, // Revalidate every minute
    };
  } catch (error) {
    console.error("Error in getStaticProps:", error);
    // Return empty data to prevent build failure
    return {
      props: { 
        Job: {}, 
        id: params.jobId 
      },
      revalidate: 60,
    };
  }
}